import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { TextField } from '@anyemp/ui-kit';
import useDebounce from '../../hooks/useDebounce';

/**
 * Reusable SearchInput component with debounce functionality.
 * Public API unchanged — onChange is called with the debounced VALUE (not event).
 */
const SearchInput = ({
  value,
  onChange,
  placeholder = 'Search...',
  debounceTime = 300,
  showIcon = true,
  className = '',
  disabled = false,
  ...props
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef(null);
  const debouncedOnChange = useDebounce((v) => {
    onChange(v);
  }, debounceTime);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const next = e.target.value;
    setLocalValue(next);
    debouncedOnChange(next);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    inputRef.current?.focus();
  };

  const focusInput = () => inputRef.current?.focus();

  return (
    <TextField
      ref={inputRef}
      type="text"
      fullWidth
      size="small"
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      startAdornment={
        showIcon ? (
          <span
            onClick={focusInput}
            className="inline-flex items-center justify-center cursor-text text-light-text-tertiary dark:text-dark-text-tertiary"
            aria-hidden="true"
          >
            <Search size={14} />
          </span>
        ) : undefined
      }
      endAdornment={
        localValue && !disabled ? (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="inline-flex items-center justify-center text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
          >
            <X size={14} />
          </button>
        ) : undefined
      }
      sx={{ root: className }}
      {...props}
    />
  );
};

export default SearchInput;
